import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { projets as apiProjets, taches as apiTaches, messageErreur } from '../services/api';
import { STATUTS_PROJET, libelle } from '../constants';
import Kanban from '../components/Kanban';
import Fresque from '../components/Fresque';
import FicheTache from '../components/FicheTache';
import { ModaleProjet } from '../components/Formulaires';
import {
  Avancement, Avatar, BadgeEntreprise, Chargement, ChoixEntreprise, ChoixPersonnes, ChoixUtilisateur, Erreur, useReferentiels,
  IconeCalendrier,
} from '../components/communs';
import { dateCourte, enRetard, nomComplet } from '../utils/format';

const CLE_VUE = 'a97projets_vue_projets';
const VUES = [['kanban', 'Kanban'], ['liste', 'Liste'], ['fresque', 'Fresque']];
const lireVue = () => { try { const v = localStorage.getItem(CLE_VUE); return VUES.some(([k]) => k === v) ? v : 'kanban'; } catch { return 'kanban'; } };

function CarteProjet({ p }) {
  return (
    <>
      <div className="kanban-titre">{p.nom}</div>
      {p.entreprise_nom && <div><BadgeEntreprise nom={p.entreprise_nom} type={p.entreprise_type} /></div>}
      <Avancement fait={p.nb_terminees} total={p.nb_taches} />
      <div className="carte-ligne">
        <span className="carte-meta">
          {p.date_echeance && <span className={enRetard(p.date_echeance, p.statut) ? 'retard' : ''}><IconeCalendrier /> {dateCourte(p.date_echeance)}</span>}
          {p.nb_en_retard > 0 && <span className="badge badge-erreur badge-sans-point" title="Tâches en retard">{p.nb_en_retard} en retard</span>}
          {p.nb_haute > 0 && <span className="badge badge-alerte badge-sans-point" title="Tâches ouvertes en priorité haute">{p.nb_haute} haute</span>}
        </span>
        <Avatar prenom={p.responsable_prenom} nom={p.responsable_nom} titre={`Responsable : ${nomComplet(p, 'responsable_')}`} />
      </div>
    </>
  );
}

export default function Projets({ moi }) {
  const ref = useReferentiels();
  const [params, setParams] = useSearchParams();
  const naviguer = useNavigate();
  const [liste, setListe] = useState(null);
  const [fresque, setFresque] = useState(null); // { projets, taches } pour la vue Fresque
  const [erreur, setErreur] = useState(null);
  const [vue, setVue] = useState(lireVue);
  const [tacheOuverte, setTacheOuverte] = useState(null);
  const [q, setQ] = useState('');
  const entrepriseId = params.get('entreprise') ? Number(params.get('entreprise')) : null;
  const responsableId = params.get('responsable') ? Number(params.get('responsable')) : null;
  // « Voir les projets de… » : dans l'adresse, donc conservé d'une vue à l'autre.
  // Sans réglage, on arrive sur SES projets (responsable ou membre) ;
  // « membres=tous » affiche tout ce que l'on a le droit de voir.
  const membresBrut = params.get('membres');
  const tousLeMonde = membresBrut === 'tous';
  const membresParam = membresBrut === null ? String(moi.id) : tousLeMonde ? '' : membresBrut;
  const membres = useMemo(() => membresParam.split(',').map(Number).filter((n) => Number.isInteger(n) && n > 0), [membresParam]);
  const mesProjets = membres.length === 1 && membres[0] === moi.id;
  const archives = params.get('archives') === '1';
  const nouveau = params.get('nouveau') === '1';

  const filtre = (cle, valeur) => {
    const p = new URLSearchParams(params);
    if (valeur) p.set(cle, valeur); else p.delete(cle);
    setParams(p, { replace: true });
  };

  const charger = useCallback(() => {
    setErreur(null);
    const filtres = {
      entreprise_id: entrepriseId || undefined, responsable_id: responsableId || undefined,
      membres: membresParam || undefined, archives: archives ? 1 : undefined, q: q || undefined,
    };
    if (vue === 'fresque') {
      return apiProjets.fresque(filtres)
        .then((d) => { setFresque(d); setListe(d.projets); })
        .catch((e) => setErreur(messageErreur(e)));
    }
    return apiProjets.liste(filtres).then(setListe).catch((e) => setErreur(messageErreur(e)));
  }, [entrepriseId, responsableId, membresParam, archives, q, vue]);

  useEffect(() => { const t = setTimeout(charger, q ? 250 : 0); return () => clearTimeout(t); }, [charger, q]);

  function changerVue(v) {
    setVue(v);
    try { localStorage.setItem(CLE_VUE, v); } catch { /* préférence locale facultative */ }
  }

  // Glisser-déposer : mise à jour immédiate à l'écran, puis serveur ; en cas
  // de refus (droits), on recharge la vérité.
  async function deplacer(p, statut, position) {
    setListe((l) => {
      const autres = l.filter((x) => x.id !== p.id);
      const colonne = autres.filter((x) => x.statut === statut).sort((a, b) => a.position - b.position);
      colonne.splice(position, 0, { ...p, statut });
      const rangs = new Map(colonne.map((x, i) => [x.id, i]));
      return [...autres.filter((x) => x.statut !== statut), ...colonne].map((x) => (rangs.has(x.id) ? { ...x, statut, position: rangs.get(x.id) } : x));
    });
    try {
      await apiProjets.deplacer(p.id, statut, position);
    } catch (e) {
      setErreur(messageErreur(e));
      charger();
    }
  }

  // Fresque : barre déplacée ou étirée. Affichage immédiat, puis serveur ; en
  // cas de refus, on recharge la vérité.
  async function changerDates(t, dateDebut, dateEcheance) {
    const maj = (l) => l.map((x) => (x.id === t.id ? { ...x, date_debut: dateDebut, date_echeance: dateEcheance } : x));
    setFresque((f) => ({ ...f, taches: maj(f.taches) }));
    try {
      await apiTaches.dates(t.id, dateDebut, dateEcheance);
    } catch (e) {
      setErreur(messageErreur(e));
      charger();
    }
  }

  const peutDeplacer = (p) => moi.role === 'admin' || p.responsable_id === moi.id;

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Projets</h1>
          <p>
            {mesProjets ? 'Les projets dont vous êtes responsable ou membre.'
              : membres.length ? 'Les projets des personnes choisies.'
                : moi.role === 'admin' ? 'Tous les projets du groupe.' : 'Tous les projets auxquels vous avez accès.'}{' '}
            {vue === 'fresque' ? 'Une ligne par tâche, groupées par projet. Glissez une barre pour la déplacer, tirez un bord pour changer le début ou l’échéance ; cliquez pour l’ouvrir.' : vue === 'kanban' ? 'Glissez une carte pour changer son statut.' : ''}
          </p>
        </div>
        <div className="page-header-actions">
          <ChoixPersonnes utilisateurs={ref.utilisateurs} valeurs={membres} moiId={moi.id}
            onChange={(ids) => filtre('membres', ids.length === 1 && ids[0] === moi.id ? null : ids.length ? ids.join(',') : 'tous')} />
          <div className="segmente" role="group" aria-label="Affichage">
            {VUES.map(([k, l]) => (
              <button key={k} type="button" className={vue === k ? 'actif' : ''} aria-pressed={vue === k} onClick={() => changerVue(k)}>{l}</button>
            ))}
          </div>
          <button type="button" className="btn btn-primaire" onClick={() => filtre('nouveau', '1')}>Nouveau projet</button>
        </div>
      </div>

      <div className="toolbar">
        <input className="recherche" type="search" placeholder="Rechercher un projet, une concession, un client…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Rechercher" />
        <div style={{ minWidth: 200 }}>
          <ChoixEntreprise entreprises={ref.entreprises} value={entrepriseId} onChange={(v) => filtre('entreprise', v)} vide="Toutes les entreprises" />
        </div>
        <div style={{ minWidth: 180 }}>
          <ChoixUtilisateur utilisateurs={ref.utilisateurs} value={responsableId} onChange={(v) => filtre('responsable', v)} vide="Tous les responsables" />
        </div>
        <label className="bascule" style={{ margin: 0 }}>
          <input type="checkbox" checked={archives} onChange={(e) => filtre('archives', e.target.checked ? '1' : null)} />Archivés
        </label>
      </div>

      {(erreur || ref.erreur) && <Erreur onReessayer={charger}>{erreur || ref.erreur}</Erreur>}
      {!liste && !erreur && <Chargement />}
      {liste && liste.length === 0 && (
        <div className="vide carte">
          {mesProjets && !(q || entrepriseId || responsableId || archives)
            ? <>Aucun projet ne vous concerne pour l’instant. <button type="button" className="btn" style={{ marginLeft: 8 }} onClick={() => filtre('membres', 'tous')}>Voir tous les projets</button></>
            : q || entrepriseId || responsableId || membres.length || archives
            ? 'Aucun projet ne correspond à ces filtres.'
            : <>Aucun projet pour l’instant. <button type="button" className="btn btn-primaire" style={{ marginLeft: 8 }} onClick={() => filtre('nouveau', '1')}>Créer le premier projet</button></>}
        </div>
      )}
      {liste && liste.length > 0 && vue === 'kanban' && (
        <Kanban
          colonnes={STATUTS_PROJET}
          elements={liste}
          rendreCarte={(p) => <CarteProjet p={p} />}
          onOuvrir={(p) => naviguer(`/projets/${p.id}`)}
          onDeplacer={deplacer}
          peutDeplacer={peutDeplacer}
          videTexte="Aucun projet"
        />
      )}
      {liste && liste.length > 0 && vue === 'fresque' && fresque && (
        <Fresque
          projets={fresque.projets}
          taches={fresque.taches}
          onOuvrirProjet={(p) => naviguer(`/projets/${p.id}`)}
          onOuvrirTache={(t) => setTacheOuverte(t.id)}
          onChangerDates={changerDates}
        />
      )}
      {liste && liste.length > 0 && vue === 'liste' && (
        <div className="table-container">
          <table className="table-cartes">
            <thead>
              <tr><th>Projet</th><th>Concession / client</th><th>Responsable</th><th>Statut</th><th>Échéance</th><th style={{ width: 180 }}>Avancement</th></tr>
            </thead>
            <tbody>
              {liste.map((p) => (
                <tr key={p.id} className="cliquable" onClick={() => naviguer(`/projets/${p.id}`)}>
                  <td style={{ fontWeight: 600 }}>{p.nom}</td>
                  <td>{p.entreprise_nom ? <BadgeEntreprise nom={p.entreprise_nom} type={p.entreprise_type} /> : <span className="faible">—</span>}</td>
                  <td>{nomComplet(p, 'responsable_') || <span className="faible">—</span>}</td>
                  <td>{libelle(STATUTS_PROJET, p.statut)}</td>
                  <td className={enRetard(p.date_echeance, p.statut) ? 'retard' : ''}>{dateCourte(p.date_echeance) || <span className="faible">—</span>}</td>
                  <td><Avancement fait={p.nb_terminees} total={p.nb_taches} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tacheOuverte && (
        <FicheTache
          tacheId={tacheOuverte}
          moi={moi}
          utilisateurs={ref.utilisateurs}
          lienProjet
          onFermer={() => setTacheOuverte(null)}
          onModifie={charger}
        />
      )}
      {nouveau && ref.pret && (
        <ModaleProjet
          moi={moi}
          utilisateurs={ref.utilisateurs}
          entreprises={ref.entreprises}
          onFermer={() => filtre('nouveau', null)}
          enregistrer={(v) => apiProjets.creer(v)}
          onEnregistre={(r) => naviguer(`/projets/${r.id}`)}
        />
      )}
    </div>
  );
}
